import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Phone,
  Mail,
  Building,
  Eye,
  Edit,
  Search,
  Package,
  Plus,
  X,
  ChevronDown,
  Filter,
  Download,
  IndianRupee,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload,
  UserPlus,
  Lock as LockIcon,
  Unlock as UnlockIcon,
  Ban,
  Slash
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import PermissionGate from '../../../components/common/PermissionGate';
import customerService from '../../../api/crm/customers/customerService';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import ExportModal from '../../../components/common/ExportModal';
import { generateExportFilename, downloadFromBackend, exportToCSV } from '../../../utils/exportUtils';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import BulkCustomerUpload from './BulkCustomerUpload';
import SendEmailModal from '../../../components/crm/SendEmailModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import teamManagementService from '../../../api/crm/team-management/teamManagementService';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import usePermissions from '../../../hooks/usePermissions';

const Customer360List = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { canRead, canUpdate, canCreate, canDelete } = usePermissions();

  // Permissions based on granular permission system
  const hasFullReadAccess = canRead('customer_management');
  const isSalesExecutive = user?.role === 'sales_executive' || user?.role === 'export_executive' || user?.role === 'business_relationship_manager' || user?.role === 'business_development_executive';
  const isManager = user?.role === 'sales_manager' || user?.role === 'export_manager';

  // IMPORTANT: user.id = User._id (always), user._id may be Employee._id.
  // Customer.assignedTo stores User._id, so always resolve via user.id first.
  const currentUserId =
    user?.id ||
    user?.userId ||
    (() => {
      try {
        const persisted = JSON.parse(localStorage.getItem('user_data') || '{}');
        return persisted?.id || persisted?.userId || persisted?._id;
      } catch (e) {
        return user?._id;
      }
    })();

  const canAssignCustomer = canUpdate('customer_management') && !isSalesExecutive;
  const hasCreatePermission = canCreate('customer_management');
  const hasDeletePermission = canDelete('customer_management');
  
  // Assignee filter state
  // Managers default to viewing their own data on page load.
  // Executives are restricted server-side; their filterAssignee value is unused.
  const defaultAssignee = isManager && currentUserId ? currentUserId : 'all';
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterAssignee, setFilterAssignee] = useState(searchParams.get('assignee') || defaultAssignee);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [assigneeFilterSearchTerm, setAssigneeFilterSearchTerm] = useState('');
  const [debouncedAssigneeSearchTerm, setDebouncedAssigneeSearchTerm] = useState('');
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  // Debounce assignee search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAssigneeSearchTerm(assigneeFilterSearchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeFilterSearchTerm]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const searchInputRef = useRef(null);
  const [pagination, setPagination] = useState({
    page: Number(searchParams.get('page')) || 1,
    limit: Number(searchParams.get('limit')) || 10,
    total: 0
  });
  const [itemsPerPage, setItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [invoicesRange, setInvoicesRange] = useState({
    min: searchParams.get('minInvoices') || '',
    max: searchParams.get('maxInvoices') || ''
  });
  const [valueRange, setValueRange] = useState({
    min: searchParams.get('minValue') || '',
    max: searchParams.get('maxValue') || ''
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState(searchParams.get('sortBy') || 'updatedAt');
  const [sortDirection, setSortDirection] = useState(searchParams.get('sortOrder') || 'desc');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  const [debouncedFilters, setDebouncedFilters] = useState({
    minInvoices: searchParams.get('minInvoices') || '',
    maxInvoices: searchParams.get('maxInvoices') || '',
    minValue: searchParams.get('minValue') || '',
    maxValue: searchParams.get('maxValue') || ''
  });
  const [dateRange, setDateRange] = useState({
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || ''
  });

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = [
        'search', 'assignee', 'startDate', 'endDate',
        'minInvoices', 'maxInvoices', 'minValue', 'maxValue',
        'page', 'limit', 'sortBy', 'sortOrder'
      ];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterAssignee && filterAssignee !== 'all') params.set('assignee', filterAssignee);
      if (dateRange.startDate) params.set('startDate', dateRange.startDate);
      if (dateRange.endDate) params.set('endDate', dateRange.endDate);
      
      if (debouncedFilters.minInvoices) params.set('minInvoices', debouncedFilters.minInvoices);
      if (debouncedFilters.maxInvoices) params.set('maxInvoices', debouncedFilters.maxInvoices);
      if (debouncedFilters.minValue) params.set('minValue', debouncedFilters.minValue);
      if (debouncedFilters.maxValue) params.set('maxValue', debouncedFilters.maxValue);
      
      if (pagination.page && pagination.page !== 1) params.set('page', String(pagination.page));
      if (itemsPerPage && itemsPerPage !== 10) params.set('limit', String(itemsPerPage));
      if (sortColumn && sortColumn !== 'updatedAt') params.set('sortBy', sortColumn);
      if (sortDirection && sortDirection !== 'desc') params.set('sortOrder', sortDirection);

      return params;
    }, { replace: true });
  }, [
    debouncedSearchTerm,
    filterAssignee,
    dateRange,
    debouncedFilters,
    pagination.page,
    itemsPerPage,
    sortColumn,
    sortDirection,
    setSearchParams
  ]);

  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedCustomerForBlock, setSelectedCustomerForBlock] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [blockLoading, setBlockLoading] = useState(false);
  const [isBlocking, setIsBlocking] = useState(true); // true for block, false for unblock
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedCustomerForEmail, setSelectedCustomerForEmail] = useState(null);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [isBulkEmailMode, setIsBulkEmailMode] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignSearchTerm, setAssignSearchTerm] = useState('');
  const [assigneeUsers, setAssigneeUsers] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const [assignUsersLoading, setAssignUsersLoading] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [openDropdownCustomerId, setOpenDropdownCustomerId] = useState(null);
  const [rowAssigneeUsers, setRowAssigneeUsers] = useState({});
  const [rowAssigneeSearchTerms, setRowAssigneeSearchTerms] = useState({});
  const [rowDropdownPositions, setRowDropdownPositions] = useState({});
  const [rowAssignUsersLoading, setRowAssignUsersLoading] = useState(false);
  const [rowAssignSubmittingId, setRowAssignSubmittingId] = useState(null);
  const rowDropdownRefs = useRef({});



  // React Query: Fetch CRM users for assignee filter with backend search
  const {
    data: assigneeUsersData,
    isLoading: loadingAssigneeUsers,
    isFetching: isFetchingAssigneeUsers,
  } = useQuery({
    queryKey: [
      'crmAssigneeUsers',
      isManager ? (currentUserId || 'manager') : 'all',
      debouncedAssigneeSearchTerm,
    ],
    queryFn: async () => {
      const params = {
        roleFilter: 'sales',
        limit: 100,
        ...(debouncedAssigneeSearchTerm ? { search: debouncedAssigneeSearchTerm } : {}),
      };

      if (isManager) {
        params.managerId = currentUserId;
      }

      const result = await teamManagementService.getCRMUsers(params);
      return result?.success && result.users ? result.users : [];
    },
    enabled: !isSalesExecutive && (isManager ? !!currentUserId : true),
    staleTime: 0,
  });

  const loadingAssigneeFilter = loadingAssigneeUsers || isFetchingAssigneeUsers;

  // Derive filtered assignee options based on fetched data and search term
  const filteredAssigneeOptions = useMemo(() => {
    if (!assigneeUsersData) return [];

    const lowerTerm = debouncedAssigneeSearchTerm.toLowerCase();

    if (isManager) {
      const executives = assigneeUsersData
        .filter(u => ['sales_executive', 'export_executive', 'business_relationship_manager', 'business_development_executive'].includes(u.role))
        .map(u => ({
          id: u.user || u._id || u.id,
          name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
          email: u.email || '',
          role: u.role || ''
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const selfOption = {
        id: currentUserId,
        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'Me',
        email: user?.email || '',
        role: user?.role || ''
      };

      const myTeamOption = {
        id: 'my_team',
        name: 'My Team',
        email: '',
        role: 'system'
      };

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'my team'.includes(lowerTerm)) {
        prefixOptions.push(myTeamOption);
      }
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }
      if (!debouncedAssigneeSearchTerm || selfOption.name.toLowerCase().includes(lowerTerm) || (selfOption.email && selfOption.email.toLowerCase().includes(lowerTerm))) {
        prefixOptions.push(selfOption);
      }

      return [...prefixOptions, ...executives];
    } else {
      const mappedUsers = assigneeUsersData.map(u => ({
        id: u.user || u._id || u.id,
        name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown',
        email: u.email || '',
        role: u.role || ''
      })).sort((a, b) => a.name.localeCompare(b.name));

      const prefixOptions = [];
      if (!debouncedAssigneeSearchTerm || 'unassigned'.includes(lowerTerm)) {
        prefixOptions.push({ id: 'unassigned', name: 'Unassigned', role: 'system' });
      }

      return [...prefixOptions, ...mappedUsers];
    }
  }, [assigneeUsersData, debouncedAssigneeSearchTerm, isManager, currentUserId, user]);

  // Maintain assigneeOptions cache for button display label
  useEffect(() => {
    if (filteredAssigneeOptions.length > 0) {
      setAssigneeOptions(prev => {
        const map = new Map();
        prev.forEach(item => { if (item && item.id) map.set(item.id, item); });
        filteredAssigneeOptions.forEach(item => { if (item && item.id) map.set(item.id, item); });
        if (map.size === prev.length) return prev;
        return Array.from(map.values());
      });
    }
  }, [filteredAssigneeOptions]);

  // Calculate assignee dropdown position
  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if (!dropdownElement || !dropdownElement.contains(event.target)) {
          setOpenAssigneeDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Debounce search term
  useEffect(() => {
    if (searchTerm === debouncedSearchTerm) return;

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
      setSelectedRows(new Set()); // Reset selection on search
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, debouncedSearchTerm]);

  // Debounce numeric filters
  useEffect(() => {
    if (
      invoicesRange.min === debouncedFilters.minInvoices &&
      invoicesRange.max === debouncedFilters.maxInvoices &&
      valueRange.min === debouncedFilters.minValue &&
      valueRange.max === debouncedFilters.maxValue
    ) {
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedFilters({
        minInvoices: invoicesRange.min,
        maxInvoices: invoicesRange.max,
        minValue: valueRange.min,
        maxValue: valueRange.max
      });
      // Only reset page if filters actually changed (prevents initial double fetch)
      // For simplicity, we just reset page on any text change in inputs
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);

    return () => clearTimeout(handler);
  }, [invoicesRange, valueRange, debouncedFilters]);

  // Fetch Customer 360 KPIs using React Query
  const {
    data: kpisData,
    isLoading: kpiLoading
  } = useQuery({
    queryKey: ['customer360Kpis', user?._id, debouncedSearchTerm, filterAssignee, debouncedFilters, dateRange],
    queryFn: async ({ signal }) => {
      const result = await customerService.getCustomer360Kpis({
        search: debouncedSearchTerm.trim() || undefined,
        assignee: filterAssignee !== 'all' ? filterAssignee : undefined,
        minInvoices: debouncedFilters.minInvoices !== '' ? debouncedFilters.minInvoices : undefined,
        maxInvoices: debouncedFilters.maxInvoices !== '' ? debouncedFilters.maxInvoices : undefined,
        minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
        maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined
      }, signal);
      if (result.success) {
        return result.kpis || {
          totalCustomers: 0,
          totalInvoices: 0,
          totalValue: 0
        };
      }
      throw new Error(result.error || 'Failed to load KPIs');
    }
  });

  const kpis = kpisData || {
    totalCustomers: 0,
    totalInvoices: 0,
    totalValue: 0
  };

  // Fetch Customer 360 list data using React Query
  const {
    data: customersData,
    isLoading: loading,
    isError: isError,
    error: queryError
  } = useQuery({
    queryKey: ['customer360List', user?._id, debouncedSearchTerm, /* filterStatus, */ pagination.page, itemsPerPage, debouncedFilters, dateRange, sortColumn, sortDirection, filterAssignee],
    queryFn: async ({ signal }) => {
      const result = await customerService.getCustomer360List({
        search: debouncedSearchTerm.trim() || undefined,
        // status: filterStatus !== 'all' ? filterStatus : undefined,
        assignee: filterAssignee !== 'all' ? filterAssignee : undefined,
        minInvoices: debouncedFilters.minInvoices !== '' ? debouncedFilters.minInvoices : undefined,
        maxInvoices: debouncedFilters.maxInvoices !== '' ? debouncedFilters.maxInvoices : undefined,
        minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
        maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: pagination.page,
        limit: itemsPerPage,
        sortBy: sortColumn,
        sortOrder: sortDirection
      }, signal);

      if (result.success) {
        // Update pagination state with response data
        setPagination(prev => ({
          ...prev,
          total: result.pagination?.total || 0,
          limit: result.pagination?.limit || itemsPerPage
        }));
        return {
          customers: result.customers || [],
          pagination: result.pagination || { page: 1, limit: itemsPerPage, total: 0 }
        };
      }
      throw new Error(result.error || 'Failed to load Customer 360 data');
    }
  });

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted customers for current page
  const getSortedCustomers = (data) => {
    // Handled server-side
    return data || [];
  };

  const customers = customersData?.customers || [];
  const error = queryError?.message || null;


  // Capitalize first letter of each word
  const capitalizeText = (text) => {
    if (!text) return '';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };
  const handleViewCustomer = (customerId) => {
    // Clear search and reset pagination when navigating to detail
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
    navigate(`/customer_360/${customerId}`);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setPagination(prev => ({ ...prev, page: 1, limit }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    setSelectedRows(new Set()); // Reset selection on page change
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = customers.map(c => c._id || c.id || c.customerId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const mapCustomersForExport = (customersToExport) => {
    return customersToExport.map(customer => {
      const city = customer.billingAddress?.city || customer.shippingAddress?.city || '';
      const state = customer.billingAddress?.state || customer.shippingAddress?.state || '';
      return {
        'Customer ID': customer.customerId || 'N/A',
        'Company Name': customer.companyName || '',
        'Contact Person': customer.contactPerson || '',
        'Email': customer.email || '',
        'Phone': customer.phone || '',
        'City': city,
        'State': state,
        'GSTIN': customer.gstNumber || '',
        'PAN': customer.pan || '',
        'Status': customer.isActive === false ? 'Blocked' : 'Active',
        'Total Invoices': customer.totalInvoices || 0,
        'Total Value': (customer.totalValue || 0).toFixed(2),
        'Assignee': customer.assignedTo?.name || (customer.assignedTo?.firstName ? `${customer.assignedTo.firstName} ${customer.assignedTo.lastName || ''}`.trim() : 'Unassigned'),
        'Created Date': customer.createdAt ? formatDate(customer.createdAt, { includeTime: false }) : '',
        'Update Date': (customer.updatedAt || customer.syncedAt || customer.createdAt) ? formatDate((customer.updatedAt || customer.syncedAt || customer.createdAt), { includeTime: false }) : ''
      };
    });
  };

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }

    const raf = requestAnimationFrame(() => {
      setInlineFormatPanelEntered(true);
    });

    const handleClickOutside = (event) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(event.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify({
            module: 'customer-360',
            format,
            scope,
            filters: overrideFilters ?? {
              search: debouncedSearchTerm.trim() || undefined,
              assignee: filterAssignee !== 'all' ? filterAssignee : undefined,
              minInvoices: debouncedFilters.minInvoices !== '' ? debouncedFilters.minInvoices : undefined,
              maxInvoices: debouncedFilters.maxInvoices !== '' ? debouncedFilters.maxInvoices : undefined,
              minValue: debouncedFilters.minValue !== '' ? debouncedFilters.minValue : undefined,
              maxValue: debouncedFilters.maxValue !== '' ? debouncedFilters.maxValue : undefined,
              startDate: dateRange.startDate || undefined,
              endDate: dateRange.endDate || undefined,
            },
            page: overridePage ?? pagination.page,
            limit: overrideLimit ?? itemsPerPage,
            sortBy: sortColumn,
            sortOrder: sortDirection,
            selectedIds,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `customers-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [debouncedSearchTerm, debouncedFilters, dateRange, pagination.page, itemsPerPage, sortColumn, sortDirection, filterAssignee]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((prev) => !prev);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleCustomersSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      if (!selectedRows.size) {
        toast.error('No selected customers found to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: Array.from(selectedRows),
        });
        setSelectedRows(new Set());
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleCustomersScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const params = { scope, format };

        if (scope === 'dateRange' && meta.startDate && meta.endDate) {
          params.overrideFilters = {
            range: 'custom',
            startDate: meta.startDate,
            endDate: meta.endDate,
          };
        }

        await requestBackendExport(params);
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [requestBackendExport]
  );


  const handleBlockClick = (customer, block = true) => {
    setSelectedCustomerForBlock(customer);
    setIsBlocking(block);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const handleBlockConfirm = async () => {
    const trimmedReason = blockReason.trim();

    // Reason is required for blocking, optional for unblocking
    if (isBlocking && !trimmedReason) {
      toast.error('Reason is required for blocking');
      return;
    }

    // Min length check (3 chars) if reason is provided
    if (trimmedReason && trimmedReason.length < 3) {
      toast.error('Reason must be at least 3 characters');
      return;
    }

    // Max length check (150 chars)
    if (trimmedReason.length > 150) {
      toast.error('Reason must not exceed 150 characters');
      return;
    }

    setBlockLoading(true);
    try {
      let result;
      if (isBlocking) {
        result = await customerService.blockCustomer(selectedCustomerForBlock._id || selectedCustomerForBlock.id, trimmedReason);
      } else {
        result = await customerService.unblockCustomer(selectedCustomerForBlock._id || selectedCustomerForBlock.id, trimmedReason || 'No reason provided');
      }

      const isSuccess = result.success || (result && !result.error);
      if (isSuccess) {
        toast.success(`Customer ${isBlocking ? 'blocked' : 'unblocked'} successfully`);
        setShowBlockModal(false);
        queryClient.invalidateQueries(['customer360List']);
        queryClient.invalidateQueries(['customer360Kpis']);
      } else {
        toast.error(result.error || `Failed to ${isBlocking ? 'block' : 'unblock'} customer`);
      }
    } catch (error) {
      toast.error(`An error occurred while ${isBlocking ? 'blocking' : 'unblocking'} the customer`);
    } finally {
      setBlockLoading(false);
    }
  };

  const loadSalesUsers = async (search = '') => {
    setAssignUsersLoading(true);
    try {
      const result = await teamManagementService.getCRMUsers({
        search: (search || '').trim() || undefined,
        roleFilter: 'sales',
        limit: 50
      });

      if (!result.success) {
        setAssigneeUsers([]);
        return;
      }

      const mappedUsers = (result.users || [])
        .map((user) => ({
          id: user.user || user._id || user.id,
          name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
          email: user.email || '',
          role: user.role || '',
          designation: user.designation || ''
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setAssigneeUsers(mappedUsers);
    } catch (usersError) {
      setAssigneeUsers([]);
    } finally {
      setAssignUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!showAssignModal) return;

    const timer = setTimeout(() => {
      loadSalesUsers(assignSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [assignSearchTerm, showAssignModal]);

  const openBulkAssignModal = () => {
    if (selectedRows.size === 0) {
      toast.error('Please select at least one customer');
      return;
    }

    setSelectedAssignee(null);
    setAssignSearchTerm('');
    setShowAssignModal(true);
    loadSalesUsers('');
  };

  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedAssignee(null);
    setAssignSearchTerm('');
    setAssigneeUsers([]);
  };

  const handleAssignConfirm = async () => {
    if (!selectedAssignee) {
      toast.error('Please select an assignee');
      return;
    }

    setAssignSubmitting(true);
    try {
      const customerIds = Array.from(selectedRows);
      let successCount = 0;

      await Promise.all(customerIds.map(async (customerId) => {
        const result = await customerService.updateCustomer(customerId, {
          assignedTo: selectedAssignee,
          assignedDate: new Date().toISOString()
        });
        if (result.success) successCount += 1;
      }));

      if (successCount === 0) {
        toast.error('Failed to assign selected customers');
        return;
      }

      if (successCount < customerIds.length) {
        toast.success(`Assigned ${successCount} of ${customerIds.length} customers`);
      } else {
        toast.success(`Assigned ${successCount} customers successfully`);
      }

      setSelectedRows(new Set());

      closeAssignModal();
      queryClient.invalidateQueries(['customer360List']);
    } catch (assignError) {
      toast.error(assignError.message || 'Failed to assign customer');
    } finally {
      setAssignSubmitting(false);
    }
  };

  const getCustomerId = (customer) => customer?._id || customer?.id || customer?.customerId;

  const loadRowAssigneeUsers = async (customerId, search = '') => {
    if (!customerId) return;

    setRowAssignUsersLoading(true);
    try {
      const result = await teamManagementService.getCRMUsers({
        search: (search || '').trim() || undefined,
        roleFilter: 'sales',
        limit: 50
      });

      const mappedUsers = (result?.users || [])
        .map((user) => ({
          id: user.user || user._id || user.id,
          name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown',
          email: user.email || '',
          role: user.role || ''
        }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setRowAssigneeUsers((prev) => ({
        ...prev,
        [customerId]: mappedUsers
      }));
    } catch (usersError) {
      setRowAssigneeUsers((prev) => ({
        ...prev,
        [customerId]: []
      }));
    } finally {
      setRowAssignUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!openDropdownCustomerId) return;
    const search = rowAssigneeSearchTerms[openDropdownCustomerId] || '';
    const timer = setTimeout(() => {
      loadRowAssigneeUsers(openDropdownCustomerId, search);
    }, 300);
    return () => clearTimeout(timer);
  }, [openDropdownCustomerId, rowAssigneeSearchTerms]);

  useEffect(() => {
    if (!openDropdownCustomerId) return;

    const ref = rowDropdownRefs.current[openDropdownCustomerId];
    if (!ref) return;

    const rect = ref.getBoundingClientRect();
    setRowDropdownPositions((prev) => ({
      ...prev,
      [openDropdownCustomerId]: {
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      }
    }));
  }, [openDropdownCustomerId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdownCustomerId) return;

      const ref = rowDropdownRefs.current[openDropdownCustomerId];
      const dropdownElement = document.querySelector(`[data-dropdown-customer-id="${openDropdownCustomerId}"]`);

      if (
        ref &&
        !ref.contains(event.target) &&
        (!dropdownElement || !dropdownElement.contains(event.target))
      ) {
        setOpenDropdownCustomerId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownCustomerId]);

  const handleToggleRowAssigneeDropdown = (customer) => {
    const customerId = getCustomerId(customer);
    if (!customerId) return;

    const isOpening = openDropdownCustomerId !== customerId;
    setOpenDropdownCustomerId(isOpening ? customerId : null);

    if (isOpening && !rowAssigneeUsers[customerId]) {
      loadRowAssigneeUsers(customerId, '');
    }
  };

  const handleSingleCustomerAssign = async (customer, assigneeId) => {
    const customerId = getCustomerId(customer);
    if (!customerId || !assigneeId) return;

    setRowAssignSubmittingId(customerId);
    try {
      const result = await customerService.updateCustomer(customerId, {
        assignedTo: assigneeId,
        assignedDate: new Date().toISOString()
      });

      if (!result.success) {
        toast.error(result.error || 'Failed to assign customer');
        return;
      }

      toast.success('Customer assigned successfully');
      setOpenDropdownCustomerId(null);
      queryClient.invalidateQueries(['customer360List']);
    } catch (assignError) {
      toast.error(assignError.message || 'Failed to assign customer');
    } finally {
      setRowAssignSubmittingId(null);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Customer 360°</h1>
            <p className="text-gray-600">View and manage all customer profiles</p>
          </div>
          <div className="flex items-center space-x-3">
            <PermissionGate permission="customer_management" action="read">
                <div className="relative" ref={selectedExportWrapRef}>
                  <Button
                    variant="outline"
                    icon={<Download className="h-4 w-4" />}
                    onClick={handleExportClick}
                    disabled={!customers || customers.length === 0}
                  >
                    {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
                  </Button>

                  <SelectedExportFormatBar
                    isOpen={showInlineSelectedFormats}
                    panelEntered={inlineFormatPanelEntered}
                    onExport={handleCustomersSelectedFormatExport}
                    loading={exportLoading}
                  />
                </div>
            </PermissionGate>
            {selectedRows.size > 0 && canAssignCustomer && (
              <PermissionGate permission="customer_management" action="update">
                <Button
                  variant="outline"
                  icon={<UserPlus className="h-4 w-4" />}
                  onClick={openBulkAssignModal}
                  className="border-blue-600 text-blue-600 hover:bg-blue-50"
                >
                  Bulk Assign ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            {selectedRows.size > 0 && (
              <PermissionGate permission="customer_management" action="update">
                <Button
                  variant="outline"
                  icon={<Mail className="h-4 w-4" />}
                  onClick={() => {
                    setIsBulkEmailMode(true);
                    setSelectedCustomerForEmail(null);
                    setShowSendEmailModal(true);
                  }}
                  className="border-purple-600 text-purple-600 hover:bg-purple-50"
                >
                  Bulk Email ({selectedRows.size})
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission="customer_management" action="create">
              <Button
                variant="outline"
                icon={<Upload className="h-4 w-4" />}
                onClick={() => setShowBulkUpload(true)}
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                Bulk Upload
              </Button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <Button
                icon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/customer_360/add')}
                className="bg-agrosaf-green hover:bg-agrosaf-green-hover text-white shadow-sm"
              >
                Add Customer
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {kpiLoading ? '...' : formatIndianNumber(kpis.totalCustomers)}
              </p>
            </div>
            <User className="h-8 w-8 text-blue-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Invoice</p>
              <p className="text-2xl font-bold text-purple-600">
                {kpiLoading ? '...' : formatIndianNumber(kpis.totalInvoices)}
              </p>
            </div>
            <Package className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-green-600">
                {kpiLoading ? '...' : `₹${formatIndianAmount(kpis.totalValue || 0)}`}
              </p>
            </div>
            <IndianRupee className="h-8 w-8 text-green-600 font-bold text-2xl" />
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">

          {/* Search Bar */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              id="customer-search-input"
              name="search"
              type="text"
              placeholder="Search customers id, name, company, email, phone..."
              value={searchTerm}
              onChange={(e) => handleSearchInputChange(e, setSearchTerm, FIELD_LIMITS.search)}
              maxLength={FIELD_LIMITS.search}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
              autoComplete="off"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto" ref={dropdownRef}>

            {/* Assignee Filter Dropdown */}
            {!isSalesExecutive && (
              <div className="relative">
                <button
                  ref={assigneeDropdownRef}
                  onClick={() => {
                    const isOpening = !openAssigneeDropdown;
                    setOpenAssigneeDropdown(isOpening);
                    if (isOpening) {
                      setAssigneeFilterSearchTerm('');
                    }
                  }}
                  disabled={loadingAssigneeFilter}
                  className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm min-w-[160px]"
                >
                  <span className="truncate whitespace-nowrap mr-2">
                    {filterAssignee === 'all'
                      ? 'All Assignees'
                      : filterAssignee === 'my_team'
                        ? 'My Team'
                        : assigneeOptions.find(opt => opt.id === filterAssignee)?.name || 'All Assignees'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
                </button>

                {openAssigneeDropdown && (
                  <div
                    data-dropdown-assignee-filter
                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                    style={{
                      top: `${assigneeDropdownPosition.top}px`,
                      left: `${assigneeDropdownPosition.left}px`,
                      minWidth: '240px',
                      maxWidth: '350px',
                      width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                    }}
                  >
                    <div className="p-2 border-b border-gray-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={assigneeFilterSearchTerm}
                          onChange={(e) => setAssigneeFilterSearchTerm(e.target.value)}
                          placeholder="Search assignees..."
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                      {loadingAssigneeFilter && filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                      ) : filteredAssigneeOptions.length === 0 ? (
                        <div className="p-3 text-sm text-gray-500 text-center">No assignees found</div>
                      ) : (
                        <div className="flex flex-col">

                          {filteredAssigneeOptions.map(option => (
                            <button
                              key={option.id}
                              type="button"
                              onClick={() => {
                                setAssigneeOptions(prev => prev.some(o => o.id === option.id) ? prev : [...prev, option]);
                                setFilterAssignee(option.id);
                                setOpenAssigneeDropdown(false);
                              }}
                              className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filterAssignee === option.id ? 'bg-agrosaf-green/10 text-agrosaf-green font-medium' : 'hover:bg-gray-50 text-gray-700'}`}
                            >
                              <div className="truncate font-medium">{option.name}</div>
                              {option.email && <div className="text-xs text-gray-500 truncate">{option.email}</div>}
                              {option.role && option.role !== 'system' && <div className="text-xs text-gray-500 truncate capitalize">{option.role.replace(/_/g, ' ')}</div>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Invoices Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'invoices' ? null : 'invoices')}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${activeDropdown === 'invoices' || invoicesRange.min || invoicesRange.max
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span>Invoice Count</span>
                {(invoicesRange.min || invoicesRange.max) && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] filter-badge">
                    {(invoicesRange.min && invoicesRange.max) ? 2 : 1}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'invoices' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'invoices' && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Invoice Sorting</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          if (sortColumn === 'invoices' && sortDirection === 'asc') {
                            setSortColumn('updatedAt');
                            setSortDirection('desc');
                          } else {
                            setSortColumn('invoices');
                            setSortDirection('asc');
                          }
                        }}
                        className={`flex items-center justify-center gap-2 px-2 py-2 rounded-md text-xs font-medium border transition-all ${sortColumn === 'invoices' && sortDirection === 'asc' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500 hover:text-blue-600'}`}
                      >
                        <ArrowUp className="h-3 w-3" />
                        Lowest First
                      </button>
                      <button
                        onClick={() => {
                          if (sortColumn === 'invoices' && sortDirection === 'desc') {
                            setSortColumn('updatedAt');
                            setSortDirection('desc');
                          } else {
                            setSortColumn('invoices');
                            setSortDirection('desc');
                          }
                        }}
                        className={`flex items-center justify-center gap-2 px-2 py-2 rounded-md text-xs font-medium border transition-all ${sortColumn === 'invoices' && sortDirection === 'desc' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500 hover:text-blue-600'}`}
                      >
                        <ArrowDown className="h-3 w-3" />
                        Highest First
                      </button>
                    </div>

                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 pt-2">Filter by Range</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min Invoices</label>
                        <input
                          type="number"
                          placeholder="0"
                          value={invoicesRange.min}
                          onKeyDown={(e) => {
                            if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) >= 0)) {
                              setInvoicesRange(prev => ({ ...prev, min: val }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max Invoices</label>
                        <input
                          type="number"
                          placeholder="∞"
                          value={invoicesRange.max}
                          onKeyDown={(e) => {
                            if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || (/^\d+$/.test(val) && parseInt(val, 10) >= 0)) {
                              setInvoicesRange(prev => ({ ...prev, max: val }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Value Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveDropdown(activeDropdown === 'value' ? null : 'value')}
                className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${activeDropdown === 'value' || valueRange.min || valueRange.max
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <span>Total Value</span>
                {(valueRange.min || valueRange.max) && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-[10px]">
                    {(valueRange.min && valueRange.max) ? 2 : 1}
                  </span>
                )}
                <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'value' ? 'rotate-180' : ''}`} />
              </button>

              {activeDropdown === 'value' && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-100 p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Value Sorting</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => {
                          if (sortColumn === 'value' && sortDirection === 'asc') {
                            setSortColumn('updatedAt');
                            setSortDirection('desc');
                          } else {
                            setSortColumn('value');
                            setSortDirection('asc');
                          }
                        }}
                        className={`flex items-center justify-center gap-2 px-2 py-2 rounded-md text-xs font-medium border transition-all ${sortColumn === 'value' && sortDirection === 'asc' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-200 hover:border-green-500 hover:text-green-600'}`}
                      >
                        <ArrowUp className="h-3 w-3" />
                        Lowest First
                      </button>
                      <button
                        onClick={() => {
                          if (sortColumn === 'value' && sortDirection === 'desc') {
                            setSortColumn('updatedAt');
                            setSortDirection('desc');
                          } else {
                            setSortColumn('value');
                            setSortDirection('desc');
                          }
                        }}
                        className={`flex items-center justify-center gap-2 px-2 py-2 rounded-md text-xs font-medium border transition-all ${sortColumn === 'value' && sortDirection === 'desc' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-200 hover:border-green-500 hover:text-green-600'}`}
                      >
                        <ArrowDown className="h-3 w-3" />
                        Highest First
                      </button>
                    </div>

                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2 pt-2">Filter by Range (₹)</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Min Value</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                          <input
                            type="number"
                            placeholder="0"
                            value={valueRange.min}
                            onKeyDown={(e) => {
                              if (['-', '+', 'e', 'E'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (/^\d*\.?\d{0,2}$/.test(val) && parseFloat(val) >= 0)) {
                                setValueRange(prev => ({ ...prev, min: val }));
                              }
                            }}
                            className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            min="0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Max Value</label>
                        <div className="relative">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">₹</span>
                          <input
                            type="number"
                            placeholder="∞"
                            value={valueRange.max}
                            onKeyDown={(e) => {
                              if (['-', '+', 'e', 'E'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === '' || (/^\d*\.?\d{0,2}$/.test(val) && parseFloat(val) >= 0)) {
                                setValueRange(prev => ({ ...prev, max: val }));
                              }
                            }}
                            className="w-full pl-6 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(newRange) => {
                setDateRange(newRange);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              allowFutureDates={false}
            />

            {/* Clear Button */}
            {(searchTerm || invoicesRange.min || invoicesRange.max || valueRange.min || valueRange.max || dateRange.startDate || dateRange.endDate || filterAssignee !== defaultAssignee) && (
              <Button
                variant="ghost"
                className="text-gray-500 hover:text-red-500 hover:bg-red-50 px-2 border border-gray-300 rounded-md"
                onClick={() => {
                  setSearchTerm('');
                  setDebouncedSearchTerm('');
                  setInvoicesRange({ min: '', max: '' });
                  setValueRange({ min: '', max: '' });
                  setDateRange({ startDate: '', endDate: '' });
                  setFilterAssignee(defaultAssignee);
                  setAssigneeFilterSearchTerm('');
                  setOpenAssigneeDropdown(false);
                  setPagination(prev => ({ ...prev, page: 1 }));
                  setActiveDropdown(null);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

        </div>


      </div>


      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={customers.length > 0 && selectedRows.size === customers.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('customerId')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Customer ID</span>
                    {sortColumn === 'customerId' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Customer</span>
                    {sortColumn === 'name' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('contact')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Contact</span>
                    {sortColumn === 'contact' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('invoices')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Invoice</span>
                    {sortColumn === 'invoices' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('value')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Value</span>
                    {sortColumn === 'value' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('lastInvoiceDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Last Order</span>
                    {sortColumn === 'lastInvoiceDate' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('createdAt')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created At</span>
                    {sortColumn === 'createdAt' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('update')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Update</span>
                    {sortColumn === 'update' ? (
                      sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </th>
                {!isSalesExecutive && (
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('owner')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Assignee</span>
                      {sortColumn === 'owner' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                      <p>Loading customers...</p>
                    </div>
                  </td>
                </tr>
              ) : isError ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-red-500">
                    {error || 'Failed to load Customer 360 data'}
                  </td>
                </tr>
              ) : customers.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                    No customers found matching your search criteria.
                  </td>
                </tr>
              ) : (
                getSortedCustomers(customers).map((customer) => {
                  const displayName = customer.companyName || customer.contactPerson || 'N/A';
                  const contactName = customer.contactPerson || '';
                  const phoneFull = customer.phone || '';
                  const emailFull = customer.email || '';

                  return (
                    <tr key={customer._id || customer.id} className="hover:bg-gray-50 border-b border-gray-100 last:border-0 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          checked={selectedRows.has(customer._id || customer.id || customer.customerId)}
                          onChange={() => handleSelectRow(customer._id || customer.id || customer.customerId)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.customerId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 relative group">
                            {customer.profileImage ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover border border-gray-200 shadow-sm transition-transform group-hover:scale-110"
                                src={customer.profileImage}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-xs bg-gradient-to-br from-green-500 to-blue-600 shadow-sm ${customer.profileImage ? 'hidden' : 'flex'}`}
                              style={{ display: customer.profileImage ? 'none' : 'flex' }}
                            >
                              {(contactName || displayName || 'N/A')
                                .split(' ')
                                .filter(Boolean)
                                .map((n) => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                          </div>
                          <div className="ml-2">
                            <div
                              className="text-sm font-semibold text-gray-900 max-w-[200px] truncate capitalize cursor-pointer hover:text-agrosaf-green transition-colors"
                              title={contactName || displayName}
                              onClick={() => handleViewCustomer(customer._id || customer.id)}
                            >
                              {capitalizeText(contactName || displayName)}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 max-w-[200px]">
                              <Building className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate capitalize" title={displayName}>
                                {capitalizeText(displayName)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-1 max-w-xs">
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate" title={phoneFull[0]}>
                            {phoneFull[0]}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1 max-w-xs">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate" title={emailFull}>
                            {emailFull}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatIndianNumber(customer.totalInvoices || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ₹{formatIndianAmount(customer.totalValue || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.lastInvoiceDate ? (
                            (() => {
                              const diffTime = Math.max(0, new Date() - new Date(customer.lastInvoiceDate));
                              const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                              return diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
                            })()
                          ) : (
                            <span className="text-gray-500 italic">No order</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {customer.createdAt ? formatDate(customer.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {(customer.updatedAt || customer.syncedAt || customer.createdAt) ? formatDate(customer.updatedAt || customer.syncedAt || customer.createdAt, { includeTime: true }) : 'N/A'}
                        </div>
                      </td>
                      {!isSalesExecutive && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const assignee = customer.assignee || {};
                            const assigneeName = `${assignee.firstName || ''} ${assignee.lastName || ''}`.trim() || 'Unassigned';
                            const customerId = getCustomerId(customer);
                            const isOpen = openDropdownCustomerId === customerId;
                            const dropdownUsers = rowAssigneeUsers[customerId] || [];
                            const dropdownPosition = rowDropdownPositions[customerId];
                            return (
                              <div className="max-w-[220px]" ref={(el) => { rowDropdownRefs.current[customerId] = el; }}>
                                <p className="text-sm font-medium text-gray-900 truncate mb-1" title={assigneeName}>
                                  {assigneeName}
                                </p>
                                {assignee.email && (
                                  <p className="text-xs text-gray-500 truncate mb-2" title={assignee.email}>
                                    {assignee.email}
                                  </p>
                                )}

                                {canAssignCustomer && (
                                  <PermissionGate permission="customer_management" action="update">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleToggleRowAssigneeDropdown(customer);
                                      }}
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white text-sm focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                      disabled={rowAssignSubmittingId === customerId}
                                    >
                                      <span className="truncate">{assigneeName === 'Unassigned' ? 'Select Assignee' : 'Reassign'}</span>
                                      <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                  </PermissionGate>
                                )}

                                {canAssignCustomer && isOpen && dropdownPosition && (
                                  <div
                                    data-dropdown-customer-id={customerId}
                                    className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                                    style={{
                                      top: `${dropdownPosition.top}px`,
                                      left: `${dropdownPosition.left}px`,
                                      minWidth: '220px',
                                      maxWidth: '320px',
                                      width: `${Math.max(220, dropdownPosition.width)}px`
                                    }}
                                  >
                                    <div className="p-2 border-b border-gray-100">
                                      <div className="relative">
                                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                        <input
                                          type="text"
                                          value={rowAssigneeSearchTerms[customerId] || ''}
                                          onChange={(e) => {
                                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                            setRowAssigneeSearchTerms((prev) => ({
                                              ...prev,
                                              [customerId]: value
                                            }));
                                          }}
                                          placeholder="Search users..."
                                          maxLength={100}
                                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                                          autoFocus
                                        />
                                      </div>
                                    </div>

                                    <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                      {rowAssignUsersLoading && dropdownUsers.length === 0 ? (
                                        <div className="p-3 text-sm text-gray-500 text-center">Loading users...</div>
                                      ) : dropdownUsers.length === 0 ? (
                                        <div className="p-3 text-sm text-gray-500 text-center">No users found</div>
                                      ) : (
                                        <div className="flex flex-col">
                                          {dropdownUsers.map((user) => (
                                            <button
                                              key={user.id}
                                              type="button"
                                              onClick={() => handleSingleCustomerAssign(customer, user.id)}
                                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-gray-700 flex flex-col"
                                              disabled={rowAssignSubmittingId === customerId}
                                            >
                                              <div className="truncate font-medium break-words">{user.name}</div>
                                              {user.email && (
                                                <div className="text-xs text-gray-500 truncate break-words">{user.email}</div>
                                              )}
                                              {user.role && (
                                                <div className="text-xs text-gray-500 truncate capitalize break-words">{String(user.role).replace('_', ' ')}</div>
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <PermissionGate permission="customer_management" action="read">
                            <button
                              onClick={() => handleViewCustomer(customer._id || customer.id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="View Customer"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="customer_management" action="update">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!customer.email) {
                                  toast.error('No Email Found');
                                  return;
                                }
                                setSelectedCustomerForEmail(customer);
                                setShowSendEmailModal(true);
                              }}
                              className="text-purple-600 hover:text-purple-900"
                              title="Send Email"
                            >
                              <Mail className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="customer_management" action="update">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer_360/edit/${customer._id || customer.id}`);
                              }}
                              className="text-agrosaf-green hover:text-agrosaf-green-hover"
                              title="Edit Customer"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="customer_management" action="update">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBlockClick(customer, !customer.isBlocked);
                              }}
                              className={customer.isBlocked ? "text-green-600 hover:text-green-900" : "text-red-600 hover:text-red-900"}
                              title={customer.isBlocked ? "Unblock Customer" : "Block Customer"}
                            >
                              {customer.isBlocked ? <UnlockIcon className="h-4 w-4" /> : <LockIcon className="h-4 w-4" />}
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(() => {
          const responsePagination = customersData?.pagination || pagination;
          const totalItems = Number(responsePagination?.total || 0);
          const totalPages = Math.ceil((responsePagination?.total || 0) / (responsePagination?.limit || itemsPerPage || 10)) || 1;
          const currentPage = responsePagination?.page || pagination.page || 1;
          const effectiveLimit = responsePagination?.limit || itemsPerPage || 10;
          const shouldShowPagination = !loading && totalPages > 1;

          if (!shouldShowPagination) return null;

          const pages = [];
          if (totalPages <= 10) {
            for (let i = 1; i <= totalPages; i++) {
              pages.push(i);
            }
          } else {
            pages.push(1);
            if (currentPage <= 4) {
              for (let i = 2; i <= 5; i++) {
                pages.push(i);
              }
              pages.push('ellipsis');
              pages.push(totalPages);
            } else if (currentPage >= totalPages - 3) {
              pages.push('ellipsis');
              for (let i = totalPages - 4; i <= totalPages; i++) {
                pages.push(i);
              }
            } else {
              pages.push('ellipsis');
              for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                pages.push(i);
              }
              pages.push('ellipsis');
              pages.push(totalPages);
            }
          }

          return (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Results Count and Limit Selector */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * effectiveLimit + 1} to {Math.min(currentPage * effectiveLimit, totalItems)} of {totalItems} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {pages.map((page, index) => {
                      if (page === 'ellipsis') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }

                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPage
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Customers"
        showDateRange
        scopedFormatExport={{
          onExport: handleCustomersScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
      {/* Bulk Upload Modal */}
      <BulkCustomerUpload
        isOpen={showBulkUpload}
        onCancel={() => setShowBulkUpload(false)}
        onComplete={() => {
          queryClient.invalidateQueries(['customer360List']);
          queryClient.invalidateQueries(['customer360Kpis']);
        }}
      />

      {/* Modals & Popups */}
      {showSendEmailModal && (
        <SendEmailModal
          isOpen={showSendEmailModal}
          onClose={() => {
            setShowSendEmailModal(false);
            setSelectedCustomerForEmail(null);
            setIsBulkEmailMode(false);
          }}
          isBulk={isBulkEmailMode}
          bulkRecipientIds={Array.from(selectedRows)}
          customerEmail={selectedCustomerForEmail?.email}
          customerId={selectedCustomerForEmail?._id || selectedCustomerForEmail?.id}
          customerName={selectedCustomerForEmail?.companyName || selectedCustomerForEmail?.contactPerson}
          customerProfileImage={selectedCustomerForEmail?.profileImage}
        />
      )}

      {/* Block/Unblock Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 z-[100] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity !mt-0"
              aria-hidden="true"
              onClick={() => setShowBlockModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full z-[101]">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${isBlocking ? 'bg-red-100' : 'bg-green-100'} sm:mx-0 sm:h-10 sm:w-10`}>
                    {isBlocking ? <Ban className="h-6 w-6 text-red-600" /> : <UnlockIcon className="h-6 w-6 text-green-600" />}
                  </div>

                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {isBlocking ? 'Block Customer' : 'Unblock Customer'}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please enter the reason for {isBlocking ? 'blocking' : 'unblocking'} <strong>{selectedCustomerForBlock?.contactPerson || selectedCustomerForBlock?.companyName}</strong>.
                      </p>
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-sm font-medium text-gray-700">
                          Reason {isBlocking ? <span className="text-red-500">*</span> : <span className="text-gray-400 font-normal">(Optional)</span>}
                        </label>
                        <span className={`text-xs ${blockReason.length > 150 ? 'text-red-500' : 'text-gray-400'}`}>
                          {blockReason.length}/150
                        </span>
                      </div>
                      <textarea
                        rows="3"
                        maxLength="150"
                        className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm rounded-md border p-2 ${isBlocking && !blockReason.trim() ? 'border-red-300' : 'border-gray-300'
                          }`}
                        placeholder={isBlocking ? "Please provide a reason for blocking..." : "Provide a reason for unblocking (optional)..."}
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                      ></textarea>
                      {isBlocking && !blockReason.trim() && (
                        <p className="mt-1 text-xs text-red-500 italic">Reason is required for blocking.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  disabled={blockLoading}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white ${isBlocking ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50`}
                  onClick={handleBlockConfirm}
                >
                  {blockLoading ? 'Processing...' : (isBlocking ? 'Block' : 'Unblock')}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowBlockModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Customer Modal (single + bulk) */}
      {showAssignModal && canAssignCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] !mt-0">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 z-[101]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Bulk Assign Customers</h3>
              <button
                onClick={closeAssignModal}
                className="text-gray-400 hover:text-gray-600"
                disabled={assignSubmitting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Select a sales person to assign <strong>{selectedRows.size}</strong> customers.
              </p>

              <div className="relative">
                <div className="flex items-center bg-gray-50 border border-gray-300 rounded px-3 py-2">
                  <Search className="h-4 w-4 text-gray-400 mr-2" />
                  <input
                    type="text"
                    placeholder="Search sales user..."
                    className="bg-transparent border-none focus:ring-0 text-sm w-full p-0"
                    value={assignSearchTerm}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                      setAssignSearchTerm(sanitized);
                    }}
                    disabled={assignSubmitting}
                  />
                </div>

                <div className="mt-2 max-h-56 overflow-y-auto border border-gray-200 rounded">
                  {assignUsersLoading ? (
                    <div className="p-2 text-center text-xs text-gray-500">Loading users...</div>
                  ) : assigneeUsers.length > 0 ? (
                    assigneeUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${selectedAssignee === user.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'}`}
                        onClick={() => setSelectedAssignee(user.id)}
                      >
                        <div className="font-medium truncate">{user.name}</div>
                        <div className="text-xs text-gray-500 truncate">{user.email}{user.role ? ` - ${user.role}` : ''}</div>
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-center text-xs text-gray-500">No sales users found</div>
                  )}
                </div>
              </div>

              {assignSubmitting && (
                <div className="mt-4 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Assigning customer(s)...</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeAssignModal}
                disabled={assignSubmitting}
              >
                Cancel
              </Button>
              <PermissionGate permission="customer_management" action="update">
                <Button
                  variant="primary"
                  onClick={handleAssignConfirm}
                  disabled={assignSubmitting || !selectedAssignee}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {assignSubmitting ? 'Assigning...' : 'Assign All'}
                </Button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customer360List;
